import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { authActions } from '@/store/actions/auth.actions';
import { alertActions } from '@/store/actions/alert.actions';

import AlertTypeEnum from '@/utils/constants/AlertTypeEnum';
import { passwordRegexp } from '@/utils/constants/regexp';
import { getUrls } from '@/utils/urls/getUrls';

import Header from '@/views/components/Header/Header';
import AuthPageWrapper from '@/views/elements/AuthPageWrapper/AuthPageWrapper';
import Spinner from '@/views/components/Spinner/Spinner';
import FormInput from '@/views/components/FormInput/FormInput';
import AppAlert from '@/views/components/Alert/AppAlert';

import api from '@/services/api';

const ChangePassword = () => {
	const navigate = useNavigate();
	const [ loading, setLoading ] = useState(false);

	const { simpleText } = getUrls();
    
	const { 
		cancel: tCancel,
		changePassword: {
			tChangePassword, 
			tOldPassword, 
			tNewPassword, 
			tRepeatNewPassword, 
			tPasswordInfo, 
			tEnterOld, 
			tEnterNew, 
			tEnterRepeat, 
			tSetPasswordBt,
			tChangePasswordSuccess,
			tChangePasswordError,
			tChangePasswordUnknownError,
			tFieldRequired,
			errors: {
				tFieldRequired: tFieldRequiredError,
				tMinChars,
				tRules,
				tMatch,
			}},
	} = useSelector(state => state.app.dict);
	const dispatch = useDispatch();

	const validationSchema = yup.object().shape({
		oldPassword: yup
			.string()
			.required(tFieldRequiredError),
		newPassword: yup
			.string()
			.required(tFieldRequiredError)
			.min(8, tMinChars)
			.matches(
				passwordRegexp,
				tRules,
			),
		confirmPassword: yup
			.string()
			.required(tFieldRequiredError)
			.oneOf([ yup.ref('newPassword'), null ], tMatch),
	});

	const { handleSubmit, register, formState: { errors, isValid } } = useForm({
		resolver: yupResolver(validationSchema),
		defaultValues: {
			oldPassword: '',
			newPassword: '',
			confirmPassword: '',
		},
		mode: "onBlur",
	});

	const LocalInput = useCallback(({name, label, placeholder, info}) => (
		<FormInput
			name={ name }
			labelValue={ label }
			{ ...register(name) }
			inputType='password'
			placeholder={ placeholder }
			error={ errors[name] }
			info={ info }
		/>
	), [ register, errors ]);

	const onSubmit = async ({oldPassword, newPassword, confirmPassword}) => {
		setLoading(true);
		if (newPassword === confirmPassword){
			try {
				await api.post('/auth/password-change', {
					oldPassword, newPassword,
				});
				dispatch(alertActions.setAlert(AlertTypeEnum.SUCCESS.toString(), tChangePasswordSuccess));
				dispatch(authActions.signOut());
			} catch (error) {
				if(error.response && error.response.status == 400){
					dispatch(alertActions.setAlert(AlertTypeEnum.ERROR.toString(), tChangePasswordError));
				} else {
					dispatch(alertActions.setAlert(AlertTypeEnum.ERROR.toString(), tChangePasswordUnknownError));                    
				}
			} finally {
				setLoading(false);
			}
		}
	};

	useEffect( () => {
		return () => {
			// in this case returnUrl must be set after dispatch in PageWrapper
			dispatch(authActions.setLoginReturnUrl({path: simpleText, urlAccessMode: 0}));
		};
	}, []);

	return (
		<>
			<Header narrow={ false } />
			<AuthPageWrapper
				pageTitle={ tChangePassword }
				backgroundClassName='login__set-password__background'
				containerClassName='login__set-password__container'
				wrapperClassName='login__set-password__wrapper'
			>
				{
					loading && (
						<Spinner addClass='dictionary__spinner' />
					)
				}
				<AppAlert />
				<form
					onSubmit={ handleSubmit(onSubmit) }
					className='login__set-password__form'
				>
					<LocalInput
						name='oldPassword'
						label={ `${tOldPassword} *` }
						placeholder={ tEnterOld }
					/>
					<LocalInput
						name='newPassword'
						label={ `${tNewPassword} *` }
						placeholder={ tEnterNew }
						info={ tPasswordInfo }
					/>
					<LocalInput
						name='confirmPassword'
						label={ `${tRepeatNewPassword} *` }
						placeholder={ tEnterRepeat }
					/>
					<div className='login__buttons-wrapper'>
						<button
							className='button__transparent'
							onClick={ () => navigate(-1) }
						>
							{tCancel}
						</button>
						<button
							className={ `button__blue login__form--submit${!isValid ? ' disabled' : ''}` }
							type='submit'
						>
							{tSetPasswordBt}
						</button>
					</div>
					
					<p className={ 'login__set-password__note-required' }>{tFieldRequired}</p>
				</form>
			</AuthPageWrapper>
		</>
	);
};

export default ChangePassword;